import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ClipboardList, CheckCircle2, Clock, Send, ExternalLink, Lock, CircleCheck } from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import { useAuth, formatApiError } from '../../context/AuthContext';

/* /academy/courses/:slug — full course detail with modules + assignments + submissions. */
export default function CourseDetail() {
  const { slug } = useParams();
  const { api } = useAuth();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null); // assignment_id

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/academy/my/courses/${slug}`);
      setCourse(data);
      setError('');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, slug]);

  useEffect(() => { reload(); }, [reload]);

  const submitAssignment = async (assignment_id, body) => {
    setSubmitting(assignment_id);
    try {
      await api.post(`/api/academy/my/assignments/${assignment_id}/submit`, { body });
      await reload();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(null);
    }
  };

  const [completing, setCompleting] = useState(null); // lesson_id
  const completeLesson = async (lesson_id) => {
    setCompleting(lesson_id);
    try {
      const { data } = await api.post(`/api/academy/my/lessons/${lesson_id}/complete`);
      // Merge in the new completion without a full reload — keeps scroll position.
      setCourse((c) => c ? { ...c, completed_lesson_ids: data.completed_lesson_ids } : c);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCompleting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <PortalTopBar />
        <main className="max-w-[900px] mx-auto px-6 py-12 text-[#6E6E73]">Loading…</main>
      </div>
    );
  }
  if (error || !course) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <PortalTopBar />
        <main className="max-w-[900px] mx-auto px-6 py-12">
          <Link to="/academy/courses" className="text-[13px] text-[#c9a227] hover:underline">← My Courses</Link>
          <div className="text-red-600 mt-4">{error || 'Course not found'}</div>
        </main>
      </div>
    );
  }

  const e = course.enrollment;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar />
      <main className="max-w-[1100px] mx-auto px-4 lg:px-6 py-8">
        <Link to="/academy/courses" className="inline-flex items-center gap-1.5 text-[13px] text-[#6E6E73] hover:text-[#c9a227] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> My Courses
        </Link>

        <div className="rounded-2xl overflow-hidden border border-[#E5E5E7] bg-white mb-8">
          <div className="bg-gradient-to-br from-[#1a1a1f] to-[#0e0e10] text-white px-6 py-6">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#f5a623] font-bold mb-1">{e.tier_id}</div>
            <h1 className="text-[28px] font-extrabold mb-1">{e.program_name}</h1>
            <p className="text-[13px] text-white/70">{e.schedule || 'Self-paced'} · {e.progress || 0}% complete{e.grade ? ` · Grade: ${e.grade}` : ''}</p>
            <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#e6c35a] to-[#a8871a] transition-all" style={{ width: `${e.progress || 0}%` }} />
            </div>
          </div>
        </div>

        {/* Modules */}
        <section className="mb-10">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-[#c9a227]" />
            <h2 className="text-[18px] font-extrabold text-[#1D1D1F]">Modules</h2>
          </div>
          {course.modules.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-6 text-[13.5px] text-[#9ca3af]">
              No modules published yet.
            </div>
          ) : (
            <ol className="space-y-3">
              {course.modules.map((m) => (
                <li key={m.id} data-testid={`course-module-${m.id}`} className="rounded-2xl border border-[#E5E5E7] bg-white p-5">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="w-7 h-7 rounded-full bg-[#fef8e7] text-[#a8871a] text-[12px] font-bold flex items-center justify-center">{m.order || '·'}</span>
                    <h3 className="text-[16px] font-extrabold text-[#1D1D1F]">{m.title}</h3>
                  </div>
                  {m.body_md && <p className="text-[13.5px] text-[#6E6E73] whitespace-pre-wrap leading-relaxed mt-2">{m.body_md}</p>}
                  {m.video_url && (
                    <a href={m.video_url} target="_blank" rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-[#c9a227] font-semibold hover:underline">
                      <ExternalLink className="w-3.5 h-3.5" /> Watch video
                    </a>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* Lessons (admin- and instructor-contributed teaching content) */}
        {(course.lessons && course.lessons.length > 0) && (
          <section className="mb-10" data-testid="course-lessons">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#c9a227]" />
                <h2 className="text-[18px] font-extrabold text-[#1D1D1F]">Lessons</h2>
                <span className="text-[11px] text-[#6E6E73]">{course.lessons.length} published</span>
              </div>
              <span data-testid="course-lessons-progress"
                className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-[#0e0e10] bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
                <CircleCheck className="w-3.5 h-3.5 text-emerald-600" />
                {(course.completed_lesson_ids || []).length}/{course.lessons.length} completed
              </span>
            </div>
            <ol className="space-y-3">
              {course.lessons.map((l, idx) => {
                const completedSet = new Set(course.completed_lesson_ids || []);
                const isCompleted = completedSet.has(l.id);
                // Locked iff any earlier lesson isn't yet completed.
                const earlier = course.lessons.slice(0, idx);
                const isLocked = earlier.some((p) => !completedSet.has(p.id));
                return (
                  <li key={l.id} data-testid={`course-lesson-${l.id}`}
                    data-completed={isCompleted ? 'true' : 'false'}
                    data-locked={isLocked ? 'true' : 'false'}
                    className={`rounded-2xl border bg-white p-5 transition-all
                      ${isLocked ? 'border-[#E5E5E7] opacity-55 grayscale' : isCompleted ? 'border-emerald-200 bg-emerald-50/40' : 'border-[#E5E5E7]'}`}>
                    <div className="flex items-baseline justify-between gap-3 mb-1.5 flex-wrap">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0
                          ${isCompleted ? 'bg-emerald-600 text-white' : isLocked ? 'bg-[#E5E5E7] text-[#9ca3af]' : 'bg-[#fef8e7] text-[#a8871a]'}`}>
                          {isLocked ? <Lock className="w-3.5 h-3.5" /> : isCompleted ? <CheckCircle2 className="w-4 h-4" /> : (idx + 1)}
                        </div>
                        <div>
                          {l.module_title && (
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#c9a227] mb-1">{l.module_title}</div>
                          )}
                          <h3 className="text-[16px] font-extrabold text-[#1D1D1F]">{l.title}</h3>
                        </div>
                      </div>
                      <div className="text-[11px] text-[#6E6E73] text-right">
                        by <span className="font-semibold text-[#0e0e10]">{l.created_by_email}</span>
                      </div>
                    </div>

                    {isLocked ? (
                      <p className="mt-3 text-[12.5px] text-[#9ca3af] flex items-center gap-1.5">
                        <Lock className="w-3.5 h-3.5" /> Complete the previous lesson{idx > 1 ? 's' : ''} to unlock this one.
                      </p>
                    ) : (
                      <>
                        {l.body_md && (
                          <p className="text-[13.5px] text-[#3a3a3f] whitespace-pre-wrap leading-relaxed mt-3">{l.body_md}</p>
                        )}
                        {(l.resources || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {l.resources.map((r, i) => (
                              <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-[12.5px] text-[#c9a227] font-semibold hover:underline">
                                <ExternalLink className="w-3.5 h-3.5" /> {r.label}
                                <span className="text-[10px] text-[#6E6E73] uppercase tracking-wider">· {r.kind}</span>
                              </a>
                            ))}
                          </div>
                        )}
                        <div className="mt-4 flex items-center gap-2">
                          {isCompleted ? (
                            <span data-testid={`lesson-completed-${l.id}`}
                              className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-emerald-700">
                              <CheckCircle2 className="w-4 h-4" /> Completed
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => completeLesson(l.id)}
                              disabled={completing === l.id}
                              data-testid={`lesson-complete-btn-${l.id}`}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
                              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> {completing === l.id ? 'Saving…' : 'Mark complete'}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* Assignments */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="w-5 h-5 text-[#c9a227]" />
            <h2 className="text-[18px] font-extrabold text-[#1D1D1F]">Assignments</h2>
          </div>
          {course.assignments.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-6 text-[13.5px] text-[#9ca3af]">
              No assignments yet.
            </div>
          ) : (
            <ul className="space-y-3">
              {course.assignments.map((a) => (
                <Assignment key={a.id} a={a} submitting={submitting === a.id}
                  onSubmit={(body) => submitAssignment(a.id, body)} />
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function Assignment({ a, onSubmit, submitting }) {
  const [body, setBody] = useState('');
  const sub = a.my_submission;
  const graded = sub?.graded_at;
  return (
    <li className="rounded-2xl border border-[#E5E5E7] bg-white p-5" data-testid={`course-assignment-${a.id}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <h3 className="text-[16px] font-extrabold text-[#1D1D1F]">{a.title}</h3>
        <div className="flex items-center gap-2">
          {graded ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="w-3 h-3" /> Graded {sub.points != null ? `${sub.points}/${a.max_points}` : sub.grade}
            </span>
          ) : sub ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
              <Clock className="w-3 h-3" /> Submitted
            </span>
          ) : null}
        </div>
      </div>
      <div className="text-[12px] text-[#6E6E73] mb-3">{a.due_at ? `Due ${a.due_at.slice(0, 10)}` : 'No due date'} · Worth {a.max_points} pts</div>
      {a.body && <p className="text-[13.5px] text-[#6E6E73] whitespace-pre-wrap leading-relaxed mb-4">{a.body}</p>}

      {sub && (
        <div className="rounded-lg bg-[#FAFAFA] border border-[#E5E5E7] p-3 mb-3">
          <div className="text-[10px] uppercase font-bold tracking-wider text-[#6E6E73] mb-1">Your submission</div>
          <p className="text-[13px] text-[#1D1D1F] whitespace-pre-wrap">{sub.body}</p>
          {sub.feedback && (
            <div className="mt-2 pt-2 border-t border-[#E5E5E7]">
              <div className="text-[10px] uppercase font-bold tracking-wider text-[#a8871a] mb-1">Instructor feedback</div>
              <p className="text-[13px] text-[#1D1D1F]">{sub.feedback}</p>
            </div>
          )}
        </div>
      )}

      {!graded && (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(body); setBody(''); }} className="space-y-2">
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} required
            data-testid={`course-asn-input-${a.id}`}
            placeholder={sub ? "Update your submission…" : "Type your answer or paste a link…"}
            className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
          <button type="submit" disabled={submitting || !body.trim()}
            data-testid={`course-asn-submit-${a.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
            style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}>
            <Send className="w-3.5 h-3.5" /> {sub ? 'Resubmit' : 'Submit'}
          </button>
        </form>
      )}
    </li>
  );
}
